import * as React from 'react';
import { Box, Flex } from '../../CommonUI/Layout';
import { StyleSheet, css } from 'aphrodite';
import * as tracking from '../../Actions/analytics';
import { connect } from 'react-redux';
import { Common } from '../../Assets/styles/common';
import { ButtonNotification } from './..';
import { ButtonTrackingProps } from '../../Model';

export interface ButtonProps extends React.HTMLProps<HTMLDivElement> {
    secondaryButton?: boolean;
    disabled?: boolean;
    onClick?: () => void | Promise<void>;
    preventDoubleClick?: boolean;
    saveTextId?: string;
    track?: ButtonTrackingProps;
    pl?: number;
    pr?: number;
    mt?: number;
    minWidth?: number | string;
    id?: string;
}

export interface InnerButtonProps extends ButtonProps {
    trackEvent: (name: string, data: any) => void;
}

interface ButtonState {
    showMessage: boolean;
    blocking: boolean;
}

class Button extends React.Component<InnerButtonProps, ButtonState> {
    public static defaultProps = { mt: 16, pl: 20, pr: 20 };

    private mounted = true;

    constructor(props: InnerButtonProps) {
        super(props);
        this.state = { showMessage: false, blocking: false };
        this.onClick = this.onClick.bind(this);
    }

    public componentWillUnmount(): void {
        this.mounted = false;
        this.setState({ blocking: false });
    }

    public onClick() {
        if (this.props.track) {
            const { source, ...track} = this.props.track;
            this.props.trackEvent!('Integration Button Clicked', {
                context: 'Ecommscript integration app',
                location: window.location.pathname,
                integration: source,
                ...track
            });
        }
        const { onClick, preventDoubleClick, disabled } = this.props;
        if (onClick && !disabled) {
            this.setState({ showMessage: true, blocking: preventDoubleClick || false }, () => {
                Promise.resolve(onClick!())
                    .then(() => this.mounted && this.state.blocking && this.setState({ blocking: false }));
                setTimeout(() => {
                    if (this.mounted) {
                        this.setState({ showMessage: false });
                    }
                }, 2000);
            });
        }
    }

    public render() {
        const { secondaryButton, disabled, saveTextId, width, mt, pl, pr, minWidth, id } = this.props;
        const { blocking, showMessage } = this.state;
        return (
            <Box width={width} mt={mt} pl={pl} pr={pr} className={this.props.className} style={{minWidth}}>
                <Flex flexDirection={'row'} style={{ width: 'inherit' }} >
                    <div
                        id={id}
                        className={css(
                            secondaryButton ? styles.buttonSecondary : styles.button,
                            secondaryButton ? styles.buttonPseudoSecondary : styles.buttonPseudo,
                            (disabled || blocking) && styles.buttonDisabled
                            )}
                        onClick={this.onClick}
                        style={{width, minWidth}}
                    >
                        {this.props.children}
                    </div>
                    <ButtonNotification showMessage={showMessage} textId={saveTextId} />
                </Flex>
            </Box>
        );
    }
}

const styles = StyleSheet.create({
    button: {
        padding: '0 24px',
        font: Common.Font.normalText,
        backgroundColor: Common.Color.buttonBackground,
        color: Common.Color.lightTextColor,
        height: Common.Dimensions.normalHeight,
        lineHeight: Common.Dimensions.normalHeight,
        webkitBoxShadow: Common.Borders.boxShadowHover,
        boxShadow: Common.Borders.boxShadowHover,
        borderRadius: Common.Borders.radius,
        display: 'inline-block',
        letterSpacing: 'normal',
        alignSelf: 'center',
        userSelect: 'none',
        boxSizing: 'border-box',
    },
    buttonDisabled: {
        cursor: 'not-allowed',
        opacity: 0.3,
        pointerEvents: 'none',
    },
    buttonPseudo: {
        ':hover': {
            boxShadow: 'inset 0 -40px 0 0 rgba(0, 0, 0, 0.2)',
            cursor: 'pointer',
        }
    },
    buttonPseudoSecondary: {
        ':hover': {
            boxShadow: 'inset 0 -2px 0 0 #368cd7',
            color: '#2471b3',
            cursor: 'pointer',
            border: 'solid 1px #368cd7',
        }
    },
    buttonSecondary: {
        padding: '0 23px',
        font: Common.Font.normalText,
        backgroundColor: '#ffffff',
        color: '#535353',
        height: '42px',
        lineHeight: Common.Dimensions.normalHeight,
        webkitBoxShadow: 'inset 0 -2px 0 0 #d4d4d4',
        boxShadow: 'inset 0 -2px 0 0 #d4d4d4',
        borderRadius: Common.Borders.radius,
        border: 'solid 1px #d4d4d4',
        display: 'inline-block',
        letterSpacing: 'normal',
        alignSelf: 'center',
        userSelect: 'none',
        boxSizing: 'border-box',
    }
});

const mapDispatchToProps = (dispatch: any) => ({
    trackEvent: (name: string, data: any) => dispatch(tracking.track(name, data)),
});

export default connect(null, mapDispatchToProps)(Button);
